import type { ReactNode, Ref } from "react";

/**
 * THE App page: the `.page.v2` column, its one `<main>`, and its one heading.
 * Every App view, the route-loading state and the error route render their
 * page through this, inside the single `AppShell` that `Root` owns
 * (views/Root). The `<main>` landmark lives here and nowhere else in App
 * (scripts/lib/one-way-contracts.js, main-landmark).
 *
 * `headingRef` makes the heading a programmatic focus target (tabIndex -1):
 * when a state replaces the page, focus moves to it so the new state is
 * announced from its own name (web v2 03 "Focus and announcements"). Without
 * one the heading is plain text and is never focusable.
 *
 * `sub` sits with the heading in `.w2-head`, 8px under it, not the column's
 * gap: a ready `<p>`, so a view keeps its own test id or conditional on it.
 *
 * BUNDLE SHAPE: the entry chunk (router's loading state, Error) and the lazy
 * views share this module, so it imports nothing at runtime but React's JSX
 * runtime. Reaching i18n, react-dom or the shell from here splits React out of
 * the entry chunk (see views/Root/footerTarget.ts and
 * docs/architecture/repo-structure.md); views translate and pass strings in.
 */
export function Page({
  title,
  sub,
  headingRef,
  className,
  testId,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  headingRef?: Ref<HTMLHeadingElement>;
  /** Extra classes on the column, beside `page v2` (e.g. `v3-login`). */
  className?: string;
  /** `data-testid` on the `<main>`. */
  testId?: string;
  children?: ReactNode;
}) {
  return (
    <div className={className ? `page v2 ${className}` : "page v2"}>
      <main data-testid={testId}>
        <div className="w2-head">
          <h1 ref={headingRef} tabIndex={headingRef ? -1 : undefined}>
            {title}
          </h1>
          {sub}
        </div>
        {children}
      </main>
    </div>
  );
}
